import React, { useCallback, useState } from 'react';
import { useMount } from 'react-use-v13';
import _ from 'lodash';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Checkbox,
} from 'design-system';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { GridColumnVisibilityModel, GridColumns } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Box from '@mui/material/Box';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  TextField,
} from 'react-scale/components/design-system/product';
import { isProjectAdmin } from '../columnConfigHelpers';
/* eslint-disable restrict-imports/restrict-imports */
import { Autocomplete } from '@material-ui/lab';
import {
  IMandatoryColumn,
  NumericConditions,
  IColorColumn,
  CustomColorClasses,
} from '@scale/shared/types/corp/speedAuditColumnConfig';
import Paper from '@mui/material/Paper';

const MandatoryColumnHeader = ({
  header,
  field,
  mandatoryColumns,
}: {
  header: string;
  field: string;
  mandatoryColumns: IMandatoryColumn;
}) => {
  const mandatoryColumn = _.get(mandatoryColumns, field);
  return (
    <Tooltip
      title={
        _.get(mandatoryColumn, 'isMandatory')
          ? `${header} is mandatory ${
              mandatoryColumn.isConditional
                ? `if the column ${_.startCase(
                    (mandatoryColumn?.conditionalColumn ?? '').replace('_editable', ''),
                  )} ${mandatoryColumn.condition} ${mandatoryColumn.conditionValue}`
                : ''
            }`
          : ''
      }
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {header}
        {_.get(mandatoryColumn, 'isMandatory') && (
          <FontAwesomeIcon
            icon="asterisk"
            style={{ marginLeft: 8, height: 10 }}
            color="rgba(0, 0, 0, 0.4)"
          />
        )}
        {_.get(mandatoryColumn, 'isMandatory') && _.get(mandatoryColumn, 'isConditional') && (
          <FontAwesomeIcon
            icon="asterisk"
            style={{ marginLeft: 8, height: 10 }}
            color="rgba(0, 0, 0, 0.4)"
          />
        )}
      </span>
    </Tooltip>
  );
};

interface ManageColumnsModalProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  columns: GridColumns;
  setColumns: React.Dispatch<React.SetStateAction<GridColumns>>;
  visibilityModel: GridColumnVisibilityModel;
  setVisibilityModel: React.Dispatch<React.SetStateAction<GridColumnVisibilityModel>>;
  setColumnConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
  mandatoryColumns: IMandatoryColumn;
  setMandatoryColumns: React.Dispatch<React.SetStateAction<IMandatoryColumn>>;
  colorColumns: IColorColumn;
  setColorColumns: React.Dispatch<React.SetStateAction<IColorColumn>>;
  projectId: string;
}

const TextBasedSpeedAuditManageColumns: React.FC<ManageColumnsModalProps> = ({
  modalOpen,
  setModalOpen,
  columns,
  setColumns,
  visibilityModel,
  setVisibilityModel,
  setColumnConfigChanged,
  mandatoryColumns: _mandatoryColumns,
  setMandatoryColumns: _setMandatoryColumns,
  colorColumns: _colorColumns,
  setColorColumns: _setColorColumns,
  projectId,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [mandatoryColumns, setMandatoryColumns] = useState<IMandatoryColumn>(_mandatoryColumns);
  const [colorColumns, setColorColumns] = useState<IColorColumn>(_colorColumns);

  const handleClose = useCallback(() => {
    _setMandatoryColumns(mandatoryColumns);
    _setColorColumns(colorColumns);
    setModalOpen(false);
  }, [setModalOpen, mandatoryColumns, colorColumns]);

  useMount(async () => {
    const admin = await isProjectAdmin(projectId);
    setIsAdmin(admin);
  });

  const handleReorder = useCallback(
    (result: DropResult) => {
      if (result.destination) {
        const newColumns = _.cloneDeep(columns);
        const [removedComponent] = newColumns.splice(result.source.index, 1);
        newColumns.splice(result.destination.index, 0, removedComponent);
        setColumns(newColumns);
        setColumnConfigChanged(true);
      }
    },
    [columns, setColumns],
  );

  const handleVisibilityChange = useCallback(
    (field: string) => {
      const curVal = _.get(visibilityModel, field, true);
      setVisibilityModel({ ...visibilityModel, [field]: !curVal });
      setColumnConfigChanged(true);
    },
    [setVisibilityModel, visibilityModel],
  );

  const handleMandatoryColumns = useCallback(
    (field: string, newValue?: boolean | string | number) => {
      let auxMandatoryColumns = _.cloneDeep(mandatoryColumns);
      auxMandatoryColumns = _.set(auxMandatoryColumns, field, newValue);
      setMandatoryColumns(auxMandatoryColumns);
      setColumnConfigChanged(true);
    },
    [mandatoryColumns],
  );

  const handleColorColumns = useCallback(
    (field: string, newValue?: boolean | string | number) => {
      let auxColorColumns = _.cloneDeep(colorColumns);
      auxColorColumns = _.set(auxColorColumns, field, newValue);
      setColorColumns(auxColorColumns);
      setColumnConfigChanged(true);
    },
    [colorColumns],
  );

  return (
    <Dialog classes={{ paper: classes.dialog }} open={modalOpen} onClose={handleClose}>
      <DialogTitle>
        <div style={{ textAlign: 'center', fontWeight: 500, fontSize: '16px', lineHeight: '20px' }}>
          Manage Columns
        </div>
      </DialogTitle>
      <DialogContent>
        <DragDropContext onDragEnd={handleReorder}>
          <Droppable droppableId="table">
            {provided => (
              <TableContainer
                component={Paper}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {/* <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table"> */}
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Column Name</TableCell>
                      <TableCell>Visible</TableCell>
                      <TableCell>Setting</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns
                      .filter(column => column.field !== 'checkbox')
                      .map((column, index) => (
                        <Draggable key={column.field} draggableId={column.field} index={index}>
                          {provided => (
                            <React.Fragment>
                              <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                                <TableCell>
                                  <div {...provided.dragHandleProps}>
                                    <FontAwesomeIcon
                                      className={classes.dragIcon}
                                      icon="grip-vertical"
                                    />
                                  </div>
                                </TableCell>

                                <TableCell>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '15px',
                                    }}
                                  >
                                    <MandatoryColumnHeader
                                      header={column.headerName ?? column.field}
                                      field={column.field}
                                      mandatoryColumns={mandatoryColumns}
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div onClick={() => handleVisibilityChange(column.field)}>
                                    <FontAwesomeIcon
                                      className={classes.visibilityIcon}
                                      icon={
                                        _.get(visibilityModel, column.field, true)
                                          ? 'eye'
                                          : 'eye-slash'
                                      }
                                      style={{
                                        color: _.get(visibilityModel, column.field, true)
                                          ? 'rgb(25, 118, 210, 1)'
                                          : '#E5E7EB',
                                      }}
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() =>
                                      setOpen(current =>
                                        current !== column.field ? column.field : '',
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon
                                      className={classes.dragIcon}
                                      icon={open === column.field ? 'chevron-up' : 'chevron-down'}
                                    />
                                  </IconButton>
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                  <Collapse in={open === column.field} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 1 }}>
                                      <Box sx={{ margin: 1 }}>
                                        <Typography variant="h6" gutterBottom component="div">
                                          Color Setting:
                                        </Typography>

                                        <div className={classes.addEditableColumnItem}>
                                          <div
                                            style={{
                                              display: 'flex',
                                              gap: '15px',
                                            }}
                                          >
                                            <Tooltip
                                              title={
                                                'Selecting this means the column would be colored'
                                              }
                                            >
                                              <div>
                                                <Checkbox
                                                  style={{
                                                    backgroundColor: '#f1f1f1',
                                                    borderRadius: '1em',
                                                    paddingRight: '1ex',
                                                  }}
                                                  label={'Colored'}
                                                  onChange={() =>
                                                    handleColorColumns(
                                                      `${column.field}.isColored`,
                                                      !_.get(
                                                        colorColumns,
                                                        `${column.field}.isColored`,
                                                      ),
                                                    )
                                                  }
                                                  disabled={!isAdmin}
                                                  checked={
                                                    !!_.get(
                                                      colorColumns,
                                                      `${column.field}.isColored`,
                                                    )
                                                  }
                                                />
                                              </div>
                                            </Tooltip>

                                            {_.get(colorColumns, `${column.field}.isColored`) && (
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  gap: '15px',
                                                }}
                                              >
                                                <Autocomplete
                                                  style={{ minWidth: '200px' }}
                                                  options={Object.values(CustomColorClasses)}
                                                  renderInput={(params: any) => (
                                                    <TextField
                                                      {...params}
                                                      size="small"
                                                      variant="outlined"
                                                      label="Primary Color"
                                                      disabled={!isAdmin}
                                                    />
                                                  )}
                                                  value={
                                                    _.get(colorColumns, `${column.field}`)
                                                      ?.primaryColor || ''
                                                  }
                                                  onChange={(event, newValue) => {
                                                    if (newValue) {
                                                      handleColorColumns(
                                                        `${column.field}.primaryColor`,
                                                        newValue,
                                                      );
                                                    }
                                                  }}
                                                />

                                                <Tooltip
                                                  title={
                                                    'Specifies the condition to use the primary color or the secondary one. If there is no condition set the condtion would be if column was filled'
                                                  }
                                                >
                                                  <div>
                                                    <Checkbox
                                                      style={{
                                                        backgroundColor: '#f1f1f1',
                                                        borderRadius: '1em',
                                                        paddingRight: '1ex',
                                                      }}
                                                      label={'Conditional'}
                                                      onChange={() =>
                                                        handleColorColumns(
                                                          `${column.field}.isConditional`,
                                                          !_.get(
                                                            colorColumns,
                                                            `${column.field}.isConditional`,
                                                          ),
                                                        )
                                                      }
                                                      disabled={!isAdmin}
                                                      checked={
                                                        !!_.get(
                                                          colorColumns,
                                                          `${column.field}.isConditional`,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </Tooltip>
                                              </div>
                                            )}
                                          </div>
                                          {_.get(colorColumns, `${column.field}.isConditional`) &&
                                            _.get(
                                              colorColumns,
                                              `${column.field}.isConditional`,
                                            ) && (
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  gap: '15px',
                                                  flexDirection: 'column',
                                                }}
                                              >
                                                <Autocomplete
                                                  style={{ minWidth: '150px' }}
                                                  options={Object.values(CustomColorClasses)}
                                                  renderInput={(params: any) => (
                                                    <TextField
                                                      {...params}
                                                      size="small"
                                                      variant="outlined"
                                                      label="Secondary Color"
                                                      disabled={!isAdmin}
                                                    />
                                                  )}
                                                  value={
                                                    _.get(colorColumns, `${column.field}`)
                                                      ?.secondaryColor || ''
                                                  }
                                                  onChange={(event, newValue) => {
                                                    if (newValue) {
                                                      handleColorColumns(
                                                        `${column.field}.secondaryColor`,
                                                        newValue,
                                                      );
                                                    }
                                                  }}
                                                />
                                                {column.type === 'number' && (
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      gap: '15px',
                                                      flexDirection: 'column',
                                                    }}
                                                  >
                                                    <Autocomplete
                                                      style={{ minWidth: '150px' }}
                                                      options={Object.values(NumericConditions)}
                                                      renderInput={(params: any) => (
                                                        <TextField
                                                          {...params}
                                                          size="small"
                                                          variant="outlined"
                                                          label="Condition"
                                                          disabled={!isAdmin}
                                                        />
                                                      )}
                                                      value={
                                                        _.get(colorColumns, `${column.field}`)
                                                          ?.condition || ''
                                                      }
                                                      onChange={(event, newValue) => {
                                                        if (newValue) {
                                                          handleColorColumns(
                                                            `${column.field}.condition`,
                                                            newValue,
                                                          );
                                                        }
                                                      }}
                                                    />

                                                    <TextField
                                                      type={'text'}
                                                      label="Conditional Value"
                                                      size="small"
                                                      variant="outlined"
                                                      value={_.get(
                                                        colorColumns,
                                                        `${column.field}.conditionValue`,
                                                      )}
                                                      disabled={!isAdmin}
                                                      onChange={(event: any) => {
                                                        handleColorColumns(
                                                          `${column.field}.conditionValue`,
                                                          event.target.value,
                                                        );
                                                      }}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </Box>
                                      {(column.field.includes('_editable') ||
                                        [
                                          'rating',
                                          'errorCategories',
                                          'notes',
                                          'taskerFeedback',
                                        ].includes(column.field)) && (
                                        <Box sx={{ margin: 1 }}>
                                          <Typography variant="h6" gutterBottom component="div">
                                            Mandatory Setting:
                                          </Typography>

                                          <div className={classes.addEditableColumnItem}>
                                            <div
                                              style={{
                                                display: 'flex',
                                                gap: '15px',
                                              }}
                                            >
                                              <Tooltip
                                                title={
                                                  'Selecting this means auditors cannot complete the audit unless all rows have a value for this column'
                                                }
                                              >
                                                <div>
                                                  <Checkbox
                                                    style={{
                                                      backgroundColor: '#f1f1f1',
                                                      borderRadius: '1em',
                                                      paddingRight: '1ex',
                                                    }}
                                                    label={'Mandatory'}
                                                    onChange={() =>
                                                      handleMandatoryColumns(
                                                        `${column.field}.isMandatory`,
                                                        !_.get(
                                                          mandatoryColumns,
                                                          `${column.field}.isMandatory`,
                                                        ),
                                                      )
                                                    }
                                                    disabled={!isAdmin}
                                                    checked={
                                                      !!_.get(
                                                        mandatoryColumns,
                                                        `${column.field}.isMandatory`,
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </Tooltip>

                                              {_.get(
                                                mandatoryColumns,
                                                `${column.field}.isMandatory`,
                                              ) && (
                                                <Tooltip
                                                  title={
                                                    'Specify the condition for which answering this column will be required. You can only create conditions based on other columns that have numeric values'
                                                  }
                                                >
                                                  <div>
                                                    <Checkbox
                                                      style={{
                                                        backgroundColor: '#f1f1f1',
                                                        borderRadius: '1em',
                                                        paddingRight: '1ex',
                                                      }}
                                                      label={'Conditional'}
                                                      onChange={() =>
                                                        handleMandatoryColumns(
                                                          `${column.field}.isConditional`,
                                                          !_.get(
                                                            mandatoryColumns,
                                                            `${column.field}.isConditional`,
                                                          ),
                                                        )
                                                      }
                                                      disabled={!isAdmin}
                                                      checked={
                                                        !!_.get(
                                                          mandatoryColumns,
                                                          `${column.field}.isConditional`,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </Tooltip>
                                              )}
                                            </div>
                                            {_.get(
                                              mandatoryColumns,
                                              `${column.field}.isMandatory`,
                                            ) &&
                                              _.get(
                                                mandatoryColumns,
                                                `${column.field}.isConditional`,
                                              ) && (
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    gap: '15px',
                                                    flexDirection: 'column',
                                                  }}
                                                >
                                                  <Autocomplete
                                                    style={{ minWidth: '150px' }}
                                                    options={columns
                                                      .filter(
                                                        c =>
                                                          c.type === 'number' &&
                                                          c.field !== column.field &&
                                                          _.get(
                                                            mandatoryColumns,
                                                            `${c.field}.isMandatory`,
                                                          ),
                                                      )
                                                      .map(
                                                        column => column.headerName ?? column.field,
                                                      )}
                                                    renderInput={(params: any) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        variant="outlined"
                                                        label="Conditional Column"
                                                        disabled={!isAdmin}
                                                      />
                                                    )}
                                                    value={
                                                      columns.find(
                                                        c =>
                                                          c.field ===
                                                          _.get(
                                                            mandatoryColumns,
                                                            `${column.field}.conditionalColumn`,
                                                            '',
                                                          ),
                                                      )?.headerName
                                                    }
                                                    onChange={(
                                                      event: any,
                                                      newValue: string | null,
                                                    ) => {
                                                      const selectedColumn = columns.find(
                                                        c =>
                                                          c.headerName === newValue ||
                                                          c.field === newValue,
                                                      );
                                                      handleMandatoryColumns(
                                                        `${column.field}.conditionalColumn`,
                                                        selectedColumn?.field,
                                                      );
                                                    }}
                                                  />

                                                  <Autocomplete
                                                    style={{ minWidth: '150px' }}
                                                    options={Object.values(NumericConditions)}
                                                    renderInput={(params: any) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        variant="outlined"
                                                        label="Condition"
                                                        disabled={!isAdmin}
                                                      />
                                                    )}
                                                    value={
                                                      _.get(mandatoryColumns, `${column.field}`)
                                                        ?.condition || ''
                                                    }
                                                    onChange={(event, newValue) => {
                                                      if (newValue) {
                                                        handleMandatoryColumns(
                                                          `${column.field}.condition`,
                                                          newValue,
                                                        );
                                                      }
                                                    }}
                                                  />

                                                  <TextField
                                                    type={'text'}
                                                    label="Conditional Value"
                                                    size="small"
                                                    variant="outlined"
                                                    value={_.get(
                                                      mandatoryColumns,
                                                      `${column.field}.conditionValue`,
                                                    )}
                                                    disabled={!isAdmin}
                                                    onChange={(event: any) => {
                                                      handleMandatoryColumns(
                                                        `${column.field}.conditionValue`,
                                                        event.target.value,
                                                      );
                                                    }}
                                                  />
                                                </div>
                                              )}
                                          </div>
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          )}
                        </Draggable>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dialog: {
    width: 800,
    overflow: 'hidden',
    margin: '2% auto 20px',
  },
  row: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  visibilityIcon: {
    cursor: 'pointer',
  },
  dragIcon: {
    color: theme.colors.gray50,
    cursor: 'grab',
  },
  addEditableColumnItem: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default TextBasedSpeedAuditManageColumns;
